import React, { useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { Asterisk, ChevronsRight, TrendingUp, ArrowLeft, ArrowRight, Sparkles, ScanSearch, ExternalLink } from 'lucide-react';
import { values, journey, MISSION_VALUES_IMAGE } from '../data/mock';
import WorldMap from './WorldMap';

export const Values = () => {
  const orderedValues = ['Mission', 'Vision', 'Values', 'Promise']
    .map((title) => values.find((item) => item.title === title))
    .filter(Boolean);

  const palettes = [
    'from-rose-50 to-rose-100/70 border-rose-200 text-rose-700',
    'from-sky-50 to-sky-100/70 border-sky-200 text-sky-700',
    'from-amber-50 to-amber-100/70 border-amber-200 text-amber-700',
    'from-violet-50 to-violet-100/70 border-violet-200 text-violet-700',
  ];

  return (
    <section id="values" className="relative py-16 lg:py-24 bg-white overflow-hidden">
      <div className="absolute -right-32 -top-32 w-[520px] h-[520px] rounded-full bg-sky-100/45 blur-3xl pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-[#C41E3A] mb-4">
              <Asterisk className="w-4 h-4" /> Mission, Vision &amp; Values
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-neutral-900 leading-[1.05]">
              What guides our work —<br /><span className="text-[#C41E3A]">beyond the paperwork</span>
            </h2>
          </div>
          <p className="lg:col-span-4 text-neutral-600 text-base md:text-lg leading-relaxed">
            A shared operating philosophy that keeps every recommendation clear, every action traceable and every outcome accountable.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-5 items-stretch">
          <figure className="lg:col-span-6 relative min-h-[560px] rounded-[2rem] overflow-hidden group shadow-[0_28px_70px_-38px_rgba(15,23,42,0.55)]">
            <img
              src={MISSION_VALUES_IMAGE}
              alt="Compliance and engineering team reviewing a regulatory roadmap"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/80 via-transparent to-transparent" />
            <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3.5 py-2 text-[10px] uppercase tracking-widest font-bold text-neutral-800 shadow-sm">
              <ScanSearch className="w-3.5 h-3.5 text-[#C41E3A]" /> Purpose in practice
            </div>
            <figcaption className="absolute left-5 right-5 bottom-5 rounded-[1.5rem] bg-white/95 backdrop-blur p-5 md:p-6">
              <div className="flex items-start gap-4">
                <span className="w-11 h-11 rounded-xl bg-[#C41E3A] text-white flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#C41E3A] font-bold mb-1.5">One shared standard</div>
                  <p className="font-display text-xl md:text-2xl font-bold text-neutral-900 leading-tight">
                    Expertise becomes valuable when teams can act on it with confidence.
                  </p>
                </div>
              </div>
            </figcaption>
          </figure>

          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-5">
            {orderedValues.map((v, i) => {
              const Icon = Icons[v.icon] || Icons.Sparkles;
              return (
                <article
                  key={v.title}
                  className={`group relative rounded-[1.75rem] bg-gradient-to-br border p-6 md:p-7 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_45px_-28px_rgba(15,23,42,0.35)] ${palettes[i]}`}
                >
                  <div className="absolute -right-8 -bottom-10 opacity-[0.08] group-hover:opacity-[0.14] group-hover:scale-110 transition-all duration-500">
                    <Icon className="w-36 h-36" />
                  </div>
                  <div className="relative flex items-start justify-between mb-12">
                    <span className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="font-display text-sm font-extrabold opacity-50">0{i + 1}</span>
                  </div>
                  <h3 className="relative font-display text-2xl font-extrabold text-neutral-900 mb-3">{v.title}</h3>
                  <p className="relative text-sm leading-relaxed text-neutral-700 max-w-sm">{v.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Journey = () => {
  const scrollRef = useRef(null);
  const dragState = useRef({ active: false, startX: 0, scrollLeft: 0 });
  const [dragging, setDragging] = useState(false);

  const scrollByCard = (direction) => {
    scrollRef.current?.scrollBy({ left: direction * 350, behavior: 'smooth' });
  };

  const startDrag = (event) => {
    if (event.pointerType !== 'mouse') return;
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = { active: true, startX: event.clientX, scrollLeft: el.scrollLeft };
    setDragging(true);
    el.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!dragState.current.active || !scrollRef.current) return;
    scrollRef.current.scrollLeft = dragState.current.scrollLeft - (event.clientX - dragState.current.startX);
  };

  const stopDrag = () => {
    dragState.current.active = false;
    setDragging(false);
  };

  return (
  <section id="journey" className="relative py-16 lg:py-24 bg-[#FDFBF7] text-neutral-900 overflow-x-clip">
    {/* Ambient tint */}
    <div className="absolute top-1/2 -translate-y-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-rose-100/50 blur-3xl" />
    <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-amber-50/60 blur-3xl" />

    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-8 mb-10 items-end">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-sm font-medium text-[#C41E3A] mb-4">
            <Asterisk className="w-4 h-4" /> Our Journey
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.05] text-neutral-900">
            Built step by step into a structured <span className="text-[#C41E3A]">compliance partner</span>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-neutral-600 text-base md:text-lg leading-relaxed mb-5">
            From a focused proprietorship in 2018 to a private limited compliance execution partner serving modern brands.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Drag timeline or use arrows</span>
            <button type="button" onClick={() => scrollByCard(-1)} className="w-9 h-9 rounded-full bg-white border border-neutral-200 hover:border-[#C41E3A] hover:text-[#C41E3A] flex items-center justify-center transition-colors" aria-label="Previous milestone">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => scrollByCard(1)} className="w-9 h-9 rounded-full bg-[#C41E3A] text-white border border-[#C41E3A] hover:bg-[#A81830] flex items-center justify-center transition-colors" aria-label="Next milestone">
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onPointerLeave={stopDrag}
        className={`timeline-scroll overflow-x-auto overscroll-x-contain snap-x snap-mandatory pb-5 ${dragging ? 'dragging' : ''}`}
        aria-label="Complise India journey timeline"
      >
            <div className="flex min-w-max gap-5 px-1">
              {journey.map((j, i) => (
                <div key={j.year} className="group relative w-[290px] sm:w-[330px] shrink-0 snap-start pt-11">
                  <div className="absolute left-0 right-[-1.25rem] top-4 flex items-center">
                    <span className="relative w-4 h-4 rounded-full bg-[#C41E3A] ring-4 ring-[#FDFBF7] z-10 shadow-md shrink-0">
                      {i === 0 && <span className="absolute inset-[-7px] rounded-full border border-[#C41E3A]/30 animate-ping" />}
                    </span>
                    {i < journey.length - 1 && <span className="h-0.5 flex-1 bg-gradient-to-r from-[#C41E3A] to-rose-200 draw-line" />}
                  </div>
                  <div className="relative bg-white border border-neutral-200 rounded-[1.75rem] p-6 hover:-translate-y-1 hover:border-[#C41E3A]/60 hover:shadow-xl transition-all overflow-hidden h-full min-h-[300px] flex flex-col">
                  {/* Corner gradient */}
                  <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-rose-50 group-hover:bg-rose-100 transition-colors" />

                  <div className="relative flex items-baseline justify-between mb-5">
                    <div className="font-display text-6xl font-extrabold text-[#C41E3A] leading-none group-hover:scale-105 transition-transform origin-left">
                      {j.year}
                    </div>
                    <span className="text-[10px] font-mono text-neutral-400 uppercase tracking-widest">0{i + 1}</span>
                  </div>

                  <h3 className="relative font-display text-xl font-bold text-neutral-900 mb-2">{j.title}</h3>
                  <p className="relative text-xs text-neutral-600 leading-relaxed mb-5 flex-1">{j.desc}</p>

                  <div className="relative flex items-center justify-between pt-4 border-t border-neutral-200">
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-[#C41E3A]">
                      <TrendingUp className="w-3 h-3" /> {j.tag}
                    </span>
                    <ChevronsRight className="w-4 h-4 text-neutral-400 group-hover:text-[#C41E3A] group-hover:translate-x-1 transition-all" />
                  </div>
                  </div>
                </div>
              ))}
            </div>
      </div>

      {/* Bottom stats strip */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { v: '2018', l: 'Year founded' },
          { v: '8+', l: 'Years of execution' },
          { v: '14', l: 'Service routes' },
          { v: '30+', l: 'Industries served' },
        ].map((s, i) => (
          <div key={i} className="border-t border-neutral-300 pt-4">
            <div className="font-display text-4xl font-extrabold text-neutral-900">{s.v}</div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1.5">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

export const GlobalReach = () => (
  <section id="global-reach" className="relative py-16 lg:py-24 bg-[#FDFBF7] text-neutral-900 overflow-hidden">
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid lg:grid-cols-12 gap-8 mb-10 items-end">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-sm font-medium text-[#C41E3A] mb-4">
            <Asterisk className="w-4 h-4" /> Global Reach
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold leading-[1.05] text-neutral-900">
            India-led execution with{' '}
            <a
              href="https://compliseglobal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-baseline gap-1 text-[#1258B5] underline decoration-[#1258B5]/25 decoration-[3px] underline-offset-[7px] transition-colors hover:text-[#0A408A] hover:decoration-[#1258B5]"
              aria-label="Visit Complise Global"
            >
              global
              <ExternalLink className="h-[0.42em] w-[0.42em] self-start mt-[0.12em] stroke-[2.5] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
            </a>{' '}
            market support
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-neutral-600 text-base md:text-lg leading-relaxed">
            Coverage includes India and major international trade routes where approvals, testing, export planning and regulatory coordination matter. Hover a country region to explore a market.
          </p>
        </div>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-2" aria-label="Associated Sharma Brother's platforms">
        <a
          href="https://compliseglobal.com"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex min-h-[118px] flex-col items-start gap-4 overflow-hidden rounded-[1.6rem] border border-blue-100 bg-gradient-to-br from-white via-white to-blue-50/80 p-5 shadow-[0_20px_55px_-38px_rgba(18,88,181,0.65)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_24px_60px_-34px_rgba(18,88,181,0.55)] sm:flex-row sm:items-center sm:gap-5"
          aria-label="Visit Complise Global website"
        >
          <span className="absolute -right-12 -top-14 h-36 w-36 rounded-full bg-blue-100/65 blur-2xl" aria-hidden="true" />
          <span className="relative flex h-[72px] w-full shrink-0 items-center overflow-hidden rounded-2xl border border-blue-100/80 bg-white px-3 shadow-sm sm:w-[220px]">
            <img src="/assets/brands/complise-global.png" alt="Complise Global" className="h-auto w-full object-contain" />
          </span>
          <span className="relative min-w-0">
            <span className="mb-1 block text-[9px] font-extrabold uppercase tracking-[0.2em] text-[#1258B5]">Global market access</span>
            <strong className="block text-base font-bold text-neutral-900">Complise Global</strong>
            <span className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-neutral-500 transition-colors group-hover:text-[#1258B5]">
              Visit platform <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </span>
          </span>
        </a>

        <a
          href="https://www.recycleveda.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex min-h-[118px] flex-col items-start gap-4 overflow-hidden rounded-[1.6rem] border border-emerald-100 bg-gradient-to-br from-white via-white to-emerald-50/80 p-5 shadow-[0_20px_55px_-38px_rgba(24,131,49,0.65)] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-[0_24px_60px_-34px_rgba(24,131,49,0.55)] sm:flex-row sm:items-center sm:gap-5"
          aria-label="Visit Recycle Veda website"
        >
          <span className="absolute -right-12 -top-14 h-36 w-36 rounded-full bg-emerald-100/65 blur-2xl" aria-hidden="true" />
          <span className="relative h-[72px] w-full shrink-0 overflow-hidden rounded-2xl border border-emerald-100/80 bg-white shadow-sm sm:w-[220px]">
            <img
              src="/assets/brands/recycle-veda.jpeg"
              alt="Recycle Veda"
              className="absolute left-1/2 top-1/2 w-full max-w-none -translate-x-1/2 -translate-y-1/2"
            />
          </span>
          <span className="relative min-w-0">
            <span className="mb-1 block text-[9px] font-extrabold uppercase tracking-[0.2em] text-[#188331]">Circular compliance</span>
            <strong className="block text-base font-bold text-neutral-900">Recycle Veda</strong>
            <span className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-neutral-500 transition-colors group-hover:text-[#188331]">
              Visit platform <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </span>
          </span>
        </a>
      </div>

      <WorldMap />
    </div>
  </section>
);
